import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import apiClient from '../utils/apiClient';

/**
 * Compare two semver strings. Returns:
 *  -1 if a < b
 *   0 if a === b
 *   1 if a > b
 */
function compareSemver(a, b) {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    const na = pa[i] || 0;
    const nb = pb[i] || 0;
    if (na < nb) return -1;
    if (na > nb) return 1;
  }
  return 0;
}

export function useAppUpdate() {
  const [updateState, setUpdateState] = useState({
    showModal: false,
    forceUpdate: false,
    latestVersion: null,
    currentVersion: null,
  });

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;

    async function checkVersion() {
      try {
        const { App } = await import('@capacitor/app');
        const info = await App.getInfo();
        const currentVersion = info.version; // e.g. "1.0.6"

        const { data } = await apiClient.get('/app/version');
        const { minVersion, latestVersion, forceUpdate } = data;

        if (cancelled) return;

        // Already on latest or newer — no update needed
        if (compareSemver(currentVersion, latestVersion) >= 0) return;

        // Below minimum version or force update flag — blocking update
        if (forceUpdate || compareSemver(currentVersion, minVersion) < 0) {
          setUpdateState({
            showModal: true,
            forceUpdate: true,
            latestVersion,
            currentVersion,
          });
          return;
        }

        // Below latest version — dismissible prompt (once per version)
        if (compareSemver(currentVersion, latestVersion) < 0) {
          const { Preferences } = await import('@capacitor/preferences');
          const { value: dismissed } = await Preferences.get({
            key: `update_dismissed_${latestVersion}`,
          });

          if (dismissed === 'true') return;

          setUpdateState({
            showModal: true,
            forceUpdate: false,
            latestVersion,
            currentVersion,
          });
        }
      } catch (err) {
        // Silently fail — don't block app usage if version check fails
        console.warn('App version check failed:', err);
      }
    }

    checkVersion();
    return () => { cancelled = true; };
  }, []);

  const dismissUpdate = async () => {
    if (updateState.latestVersion) {
      try {
        const { Preferences } = await import('@capacitor/preferences');
        await Preferences.set({
          key: `update_dismissed_${updateState.latestVersion}`,
          value: 'true',
        });
      } catch (err) {
        // Ignore preference save errors
      }
    }
    setUpdateState((prev) => ({ ...prev, showModal: false }));
  };

  return { ...updateState, dismissUpdate };
}
